import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { User, Mail, Building2, Calendar, MoreVertical } from 'lucide-react'
import { ClickToCallButton } from '@/components/ClickToCallButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Contact } from '@/types'
import { formatDate } from '@/lib/utils'

interface MobileContactCardProps {
  contact: Contact
  isSelected: boolean
  onToggleSelect: (id: string) => void
  onEdit?: (contact: Contact) => void
  onDelete?: (contact: Contact) => void
}

export function MobileContactCard({
  contact,
  isSelected,
  onToggleSelect,
  onEdit,
  onDelete,
}: MobileContactCardProps) {
  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(contact.id)}
              aria-label={`Select ${contact.name}`}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <Link
                to={`/contacts/${contact.id}`}
                className="font-semibold text-base hover:underline focus:underline focus:outline-none"
              >
                {contact.name}
              </Link>
              {contact.title && (
                <p className="text-sm text-muted-foreground mt-1">{contact.title}</p>
              )}
              {contact.lead_source && (
                <Badge variant="secondary" className="mt-1 text-xs">
                  {contact.lead_source}
                </Badge>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Actions for ${contact.name}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/contacts/${contact.id}`}>View Details</Link>
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(contact)}>Edit Contact</DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(contact)}
                  className="text-destructive focus:text-destructive"
                >
                  Delete Contact
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {contact.account_name && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{contact.account_name}</span>
          </div>
        )}

        {contact.email && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="h-4 w-4 flex-shrink-0" />
            <a
              href={`mailto:${contact.email}`}
              className="truncate hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {contact.email}
            </a>
          </div>
        )}

        {contact.phone && (
          <div
            className="flex items-center gap-2 text-muted-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            <ClickToCallButton
              phoneNumber={contact.phone}
              whoId={contact.id}
              whoType="Contact"
              whoName={`${contact.first_name} ${contact.last_name}`}
              whatId={contact.account_id}
              whatType="Account"
            />
          </div>
        )}

        {contact.owner_name && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <User className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{contact.owner_name}</span>
          </div>
        )}

        {contact.created_date && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span className="text-xs">Created {formatDate(contact.created_date)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface MobileContactListProps {
  contacts: Contact[]
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onEdit?: (contact: Contact) => void
  onDelete?: (contact: Contact) => void
}

export function MobileContactList({
  contacts,
  selectedIds,
  onToggleSelect,
  onEdit,
  onDelete,
}: MobileContactListProps) {
  return (
    <div className="space-y-3">
      {contacts.map((contact) => (
        <MobileContactCard
          key={contact.id}
          contact={contact}
          isSelected={selectedIds.has(contact.id)}
          onToggleSelect={onToggleSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}
